import i18n from 'i18next';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import { useAuthStore } from '@/shared/auth/store';
import { buildUrl } from '@/shared/api/client';
import { readBundle, writeBundle } from './cache';
import { incontextPostProcessor } from './incontext-marker';

const STORAGE_KEY = 'APP_USER_LANGUAGE';

export function getInitialLanguage(): string {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) return stored;
  const nav = navigator.language?.slice(0, 2).toLowerCase();
  return nav || 'en';
}

export function setLanguage(lang: string) {
  localStorage.setItem(STORAGE_KEY, lang);
  void i18n.changeLanguage(lang);
}

/**
 * Кому принадлежит словарь: то же, что в пространстве имён бекенда. Язык —
 * двухбуквенный, как хранит его i18next при `load: 'languageOnly'`.
 */
function currentIdentity() {
  const auth = useAuthStore.getState();
  const raw = i18n.language || getInitialLanguage();
  return {
    lng: raw.split('-')[0].toLowerCase(),
    distrId: auth.user?.distrId ?? 0,
    clientId: auth.user?.clientId ?? 0,
  };
}

/**
 * Чей словарь запрошен последним. Нужен при записи в кеш: событие `loaded`
 * приходит после ответа сети и не говорит, с какими DistrID/ClientID уходил
 * запрос, а к тому моменту пользователь мог уже переключить базу.
 */
let requested = currentIdentity();

/**
 * The legacy portal expects translations under namespace pattern
 * `{module}${distrId}${clientId}` (see portal/src/app/common/common.factory.js
 * `addNewTranslationPart`).  Backend route:
 *   GET /api/SP_Common/translations/{module$distrId$clientId}/{LANGUAGE_UPPER}
 *
 * We reuse the v1 endpoint exactly to avoid duplicating the translations
 * controller until a real centralized i18n service exists.
 */
function buildLoadPath() {
  return (lngs: string[], namespaces: string[]) => {
    const ns = namespaces[0] ?? 'common';
    // CASE MATTERS. The legacy SP compares the language code with a
    // case-sensitive `=` against rows like `ISO_LanguageCode = 'en'`, so
    // `…/EN` returns `{}` while `…/en` returns the real dictionary.
    // Verified 2026-05-13:
    //   /photo-viewer$30000$0/EN → 2 bytes
    //   /photo-viewer$30000$0/en → 5307 bytes
    const lng = (lngs[0] ?? 'en').toLowerCase();
    const auth = useAuthStore.getState();
    const distrId = auth.user?.distrId ?? 0;
    const clientId = auth.user?.clientId ?? 0;
    requested = { lng, distrId, clientId };
    const part = `${ns}$${distrId}$${clientId}`;
    return buildUrl(`/api/SP_Common/translations/${encodeURIComponent(part)}/${encodeURIComponent(lng)}`);
  };
}

/** Pages that have already had their translation namespace loaded. */
const loadedModules = new Set<string>();

/**
 * Подставляет словарь из кеша, если он там есть.
 *
 * Возвращает признак попадания: при попадании страница уже на первом кадре
 * рисуется словами, а сеть догоняет и молча обновляет.
 */
function hydrateFromCache(ns: string): boolean {
  const { lng, distrId, clientId } = currentIdentity();
  const data = readBundle({ lng, ns, distrId, clientId });
  if (!data) return false;
  // `deep: true, overwrite: false` — кеш заполняет пустое и никогда не
  // перетирает то, что уже приехало из сети.
  i18n.addResourceBundle(lng, ns, data, true, false);
  return true;
}

function persistToCache(): void {
  const { lng, distrId, clientId } = requested;
  for (const ns of loadedModules) {
    const data = i18n.getResourceBundle(lng, ns) as Record<string, string> | undefined;
    if (data) writeBundle({ lng, ns, distrId, clientId }, data);
  }
}

/** Записать в кеш словари после правки подписи на месте (`./incontext`). */
export function rememberBundles(lng: string, namespaces: string[]): void {
  const { distrId, clientId } = currentIdentity();
  for (const ns of namespaces) {
    const data = i18n.getResourceBundle(lng, ns) as Record<string, string> | undefined;
    if (data) writeBundle({ lng, ns, distrId, clientId }, data);
  }
}

/**
 * Load the translations for one legacy "module" (= the legacy page folder
 * name, e.g. `photo-viewer`).  Mirrors the AngularJS
 * `commonFactory.addNewTranslationPart` flow.  Each module becomes its own
 * i18next namespace; `fallbackNS` lets `t('DTK_FOO')` resolve across all
 * loaded namespaces in order, so callers don't have to know which module a
 * key lives in.
 *
 * Idempotent — calling twice for the same module is a cheap no-op.  Safe to
 * call from a `useEffect` on every page render.
 */
export async function loadModuleTranslations(module: string): Promise<void> {
  if (loadedModules.has(module)) return;
  loadedModules.add(module);
  // Порядок важен: сначала кеш, потом сеть. Иначе первый кадр страницы
  // показывает `DTK_*` в заголовках таблицы, даже когда словарь уже был.
  const cached = hydrateFromCache(module);
  const current = (i18n.options.fallbackNS as string[] | undefined) ?? [];
  if (!current.includes(module)) {
    i18n.options.fallbackNS = [...current, module];
  }

  if (cached) {
    // Подставленный словарь уже лежит в хранилище i18next, а `loadNamespaces`
    // такие пространства имён пропускает — поэтому ревалидация идёт
    // принудительной перезагрузкой. Страницу при этом не держим.
    void i18n.reloadResources(undefined, [module]).catch(() => {});
    return;
  }

  try {
    await i18n.loadNamespaces(module);
  } catch {
    // Сеть или 404 — страница остаётся рабочей, ключи покажутся как есть.
    loadedModules.delete(module);
  }
}

/**
 * Re-fetch every namespace already loaded into i18next.  Called after login
 * so the placeholder `{}` we received while anonymous is replaced with real
 * translations.
 */
export async function reloadAllTranslations(): Promise<void> {
  if (loadedModules.size === 0) return;
  try {
    await i18n.reloadResources(undefined, Array.from(loadedModules));
  } catch {
    // best-effort
  }
}

export async function initI18n() {
  // Словарь из прошлой загрузки отдаётся i18next сразу, вместе с init: тогда
  // первый кадр рисуется словами, а не ключами. `partialBundledLanguages`
  // оставляет бекенд рабочим для того, чего в кеше нет.
  const { lng, distrId, clientId } = currentIdentity();
  const cachedCommon = readBundle({ lng, ns: 'common', distrId, clientId });

  await i18n
    .use(HttpBackend)
    .use(initReactI18next)
    .use(incontextPostProcessor)
    .init({
      lng: getInitialLanguage(),
      fallbackLng: 'en',
      ns: ['common'],
      defaultNS: 'common',
      resources: cachedCommon ? { [lng]: { common: cachedCommon } } : undefined,
      partialBundledLanguages: Boolean(cachedCommon),
      // Cross-namespace lookup: when a key like `DTK_PHOTOVIEWER_*` isn't
      // in `common`, i18next will try each namespace in `fallbackNS` in
      // order.  Pages add to this list via `loadModuleTranslations`.
      fallbackNS: [],
      // Backend keys are flat (no `:` namespace separator, no `.` nesting).
      nsSeparator: false,
      keySeparator: false,
      load: 'languageOnly',
      interpolation: { escapeValue: false },
      // Вне режима правки подписей пост-процессор возвращает строку как есть.
      postProcess: ['ismIncontext'],
      backend: {
        loadPath: buildLoadPath(),
        // Credentials are what pick the *environment*: without the session
        // cookie the edge routes to the fallback contour, so a prod session
        // would silently read dev's dictionary. (Authorization is not the
        // issue — verified 2026-08-29 that this endpoint answers tokenless,
        // which is what lets the login page translate itself.)
        requestOptions: { credentials: 'include' },
      },
      react: {
        useSuspense: false,
        // Словарь приезжает и после первого кадра — из кеша при старте, из
        // сети при ревалидации и при смене базы. Без подписки на события
        // хранилища компоненты остались бы с тем, что успели прочитать.
        bindI18nStore: 'added',
      },
    });
  loadedModules.add('common');
  i18n.on('loaded', persistToCache);

  // Кеш мог устареть: словарь переопределяют на дистрибьютора, и правку в
  // справочнике надо увидеть без сброса браузера. Поэтому сразу за первым
  // кадром — принудительная перезагрузка ресурсов (init её пропустил, считая
  // подставленный словарь встроенным).
  if (cachedCommon) void reloadAllTranslations();

  // Re-fetch translations when the session identity changes:
  //   * login / logout — an anonymous call is answered (measured 2026-08-30:
  //     `common$0$0/ru` → 1606 keys), but with DistrID/ClientID 0 it can only
  //     return the global rows, so the per-distributor overrides are missing
  //     until we ask again with a session;
  //   * distributor / client switch — the namespace itself is
  //     `{module}${distrId}${clientId}`, and dictionaries are overridden per
  //     distributor (that is how a client renames "Route" to its own term).
  const identity = (s: { user: { userId: number; distrId: number | null; clientId: number | null } | null }) =>
    `${s.user?.userId ?? 0}|${s.user?.distrId ?? 0}|${s.user?.clientId ?? 0}`;
  let lastIdentity = identity(useAuthStore.getState());
  useAuthStore.subscribe((state) => {
    const next = identity(state);
    if (next !== lastIdentity) {
      lastIdentity = next;
      void reloadAllTranslations();
    }
  });

  return i18n;
}

export { i18n };
